export interface ToastOptions {
  type?: 'success' | 'error' | 'warning' | 'info' | 'loading'
  duration?: number
  position?: 'top' | 'bottom' | 'center'
  icon?: string
  closeOnClick?: boolean
  showClose?: boolean
}

const DEFAULT_DURATION = 3000
const DEFAULT_POSITION = 'center'

let toastContainer: HTMLElement | null = null
let activeToasts: HTMLElement[] = []

function getContainer(): HTMLElement {
  if (!toastContainer) {
    toastContainer = document.createElement('div')
    toastContainer.className = 'prl-toast-container'
    toastContainer.style.cssText = `
      position: fixed;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      align-items: center;
      pointer-events: none;
    `
    document.body.appendChild(toastContainer)
  }
  return toastContainer
}

function createToast(message: string, options: ToastOptions = {}): HTMLElement {
  const { type = 'info', position = DEFAULT_POSITION } = options

  const toast = document.createElement('div')
  toast.className = `prl-toast prl-toast-${type}`
  toast.style.cssText = `
    padding: 12px 24px;
    margin: 8px;
    border-radius: 8px;
    background: ${getTypeColor(type)};
    color: #fff;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: auto;
    animation: prl-toast-in 0.3s ease-out;
    max-width: 400px;
    word-break: break-all;
  `

  const icon = getTypeIcon(type)
  toast.innerHTML = `
    <span class="prl-toast-icon">${icon}</span>
    <span class="prl-toast-message">${message}</span>
  `

  updateContainerPosition(position)

  return toast
}

function getTypeColor(type: string): string {
  const colors: Record<string, string> = {
    success: '#52c41a',
    error: '#ff4d4f',
    warning: '#faad14',
    info: '#1890ff',
    loading: '#1890ff',
  }
  return colors[type] || colors.info
}

function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    success: '✓',
    error: '✕',
    warning: '!',
    info: 'ℹ',
    loading: '◐',
  }
  return icons[type] || icons.info
}

function updateContainerPosition(position: string): void {
  const container = getContainer()
  switch (position) {
    case 'top':
      container.style.top = '20px'
      container.style.bottom = 'auto'
      container.style.left = '50%'
      container.style.transform = 'translateX(-50%)'
      break
    case 'bottom':
      container.style.top = 'auto'
      container.style.bottom = '20px'
      container.style.left = '50%'
      container.style.transform = 'translateX(-50%)'
      break
    case 'center':
      container.style.top = '50%'
      container.style.bottom = 'auto'
      container.style.left = '50%'
      container.style.transform = 'translate(-50%, -50%)'
      break
  }
}

function removeToast(toast: HTMLElement): void {
  toast.style.animation = 'prl-toast-out 0.3s ease-out'
  setTimeout(() => {
    toast.remove()
    const index = activeToasts.indexOf(toast)
    if (index > -1) {
      activeToasts.splice(index, 1)
    }
  }, 300)
}

/**
 * 显示Toast提示
 * @description 在页面中显示一个轻量级提示消息
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @param {string} options.type - 提示类型：success | error | warning | info | loading
 * @param {number} options.duration - 显示时长（毫秒），默认3000，0表示不自动关闭
 * @param {string} options.position - 显示位置：top | bottom | center
 * @param {boolean} options.closeOnClick - 是否点击关闭，默认true
 * @example
 * // 普通提示
 * toast('操作成功')
 *
 * // 自定义配置
 * toast('加载中...', {
 *   type: 'loading',
 *   duration: 0,
 *   position: 'center'
 * })
 */
export function toast(message: string, options: ToastOptions = {}): void {
  const { duration = DEFAULT_DURATION, closeOnClick = true } = options

  const toastEl = createToast(message, options)
  getContainer().appendChild(toastEl)
  activeToasts.push(toastEl)

  if (closeOnClick) {
    toastEl.addEventListener('click', () => removeToast(toastEl))
  }

  if (duration > 0) {
    setTimeout(() => removeToast(toastEl), duration)
  }
}

/**
 * 显示成功提示
 * @description 显示绿色的成功提示消息
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @example
 * toastSuccess('保存成功')
 */
export function toastSuccess(message: string, options?: ToastOptions): void {
  toast(message, { ...options, type: 'success' })
}

/**
 * 显示错误提示
 * @description 显示红色的错误提示消息
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @example
 * toastError('操作失败，请重试')
 */
export function toastError(message: string, options?: ToastOptions): void {
  toast(message, { ...options, type: 'error' })
}

/**
 * 显示警告提示
 * @description 显示橙色的警告提示消息
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @example
 * toastWarning('请注意，数据尚未保存')
 */
export function toastWarning(message: string, options?: ToastOptions): void {
  toast(message, { ...options, type: 'warning' })
}

/**
 * 显示信息提示
 * @description 显示蓝色的信息提示消息
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @example
 * toastInfo('这是一条提示信息')
 */
export function toastInfo(message: string, options?: ToastOptions): void {
  toast(message, { ...options, type: 'info' })
}

/**
 * 显示加载提示
 * @description 显示带加载图标的提示，返回一个关闭函数
 * @param {string} message - 提示消息内容
 * @param {ToastOptions} options - 可选配置项
 * @returns {() => void} 关闭函数
 * @example
 * const close = toastLoading('加载中...')
 * // 加载完成后关闭
 * setTimeout(close, 2000)
 */
export function toastLoading(message: string, options?: ToastOptions): () => void {
  toast(message, { ...options, type: 'loading', duration: 0 })
  const loadingToast = activeToasts[activeToasts.length - 1]
  return () => removeToast(loadingToast)
}

/**
 * 清除所有Toast
 * @description 立即关闭当前所有显示的Toast提示
 * @example
 * clearToasts()
 */
export function clearToasts(): void {
  activeToasts.forEach((toast) => toast.remove())
  activeToasts = []
}
